import { useEffect } from 'react';
import { getHandoffJwt } from '@aegix/auth-core';
import { Center, Spinner, VStack, Text } from '@chakra-ui/react';

/**
 * OAuth/SSO callback page.
 *
 * The backend redirects here after SAML/OIDC authentication with a JWT token
 * delivered as either a query param (?jwt=<token>) or a path segment
 * (/oauth-success/<token>).
 *
 * Both shapes are consumed out of the URL by `consumeHandoffJwt()` in
 * `main.tsx`, before `Sentry.init` — Session Replay records
 * `location.search`/`href` synchronously at init and the replay envelope
 * never passes through `beforeSend`, so a token still in the address bar by
 * the time this component mounts has already been recorded. The path form is
 * rewritten to `/oauth-success`, which is why this route still matches. Read
 * the token from `getHandoffJwt()`, never from the URL or route params.
 *
 * This page:
 *   1. Stores the JWT in localStorage for parent-window polling
 *   2. Sends the JWT to the opener via postMessage (if popup)
 *   3. Falls back to full-page redirect with ?jwt= (if no opener)
 */
export function OAuthSuccess() {
  useEffect(() => {
    const jwt = getHandoffJwt();

    if (!jwt) {
      console.error('OAuthSuccess: no SSO handoff token for this page load');
      return;
    }

    // Store in localStorage for parent-window polling (COOP fallback)
    try {
      localStorage.setItem('sso_pending_jwt', jwt);
      localStorage.setItem('sso_jwt_timestamp', Date.now().toString());
    } catch (e) {
      console.error('OAuthSuccess: Error storing in localStorage:', e);
    }

    // Check if opener is from our domain
    let isOpenerFromOurDomain = false;
    let shouldRedirect = false;

    if (window.opener && !window.opener.closed) {
      try {
        const openerOrigin = window.opener.location.origin;
        isOpenerFromOurDomain = openerOrigin === window.location.origin;
      } catch {
        // Cross-origin — can't access opener
        shouldRedirect = true;
      }
    }

    // Send JWT to opener via postMessage
    if (
      window.opener &&
      !window.opener.closed &&
      isOpenerFromOurDomain &&
      !shouldRedirect
    ) {
      try {
        window.opener.postMessage(
          { type: 'oauth-success', token: jwt, jwt: jwt },
          '*',
        );
        setTimeout(() => window.close(), 1000);
      } catch {
        shouldRedirect = true;
      }
    }

    // No valid opener — redirect to app root with JWT
    if (
      !window.opener ||
      window.opener.closed ||
      !isOpenerFromOurDomain ||
      shouldRedirect
    ) {
      window.location.href = `${window.location.origin}/login?jwt=${jwt}`;
    }
  }, []);

  return (
    <Center minH="100vh" bg="surface.primary">
      <VStack gap={4}>
        <Spinner size="xl" color="button.primary" />
        <Text color="text.secondary">Completing authentication...</Text>
      </VStack>
    </Center>
  );
}
